// ============================================================================
// FACILITATOR INTEREST PANEL — profile page card for users who aren't
// facilitators yet: register or withdraw interest in becoming one.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { GraduationCap, Loader } from 'lucide-react';
import { getMyFacilitatorInterest, setMyFacilitatorInterest } from '../../services/studio-bookings';
import { formatDate } from './FacilitatorParts';

export function FacilitatorInterestPanel() {
  const [interest, setInterest] = useState<{ interested: boolean; addedAt?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    getMyFacilitatorInterest().then(setInterest).catch(() => setError('Failed to load'));
  }
  useEffect(load, []);

  async function toggle() {
    if (!interest) return;
    setSaving(true);
    setError(null);
    try {
      await setMyFacilitatorInterest(!interest.interested);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <GraduationCap size={18} className="text-primary" />
        </div>
        <h3 className="font-bold text-[#052831] text-lg">Become a facilitator</h3>
      </div>
      <div className="px-6 py-6 flex items-center justify-between gap-6">
        <div className="text-sm text-slate-600">
          {interest?.interested ? (
            <>
              <p className="font-semibold text-[#052831]">You're on the list of people interested in becoming a facilitator.</p>
              {interest.addedAt && <p className="text-xs text-slate-400 mt-1">Since {formatDate(interest.addedAt)}. You'll be invited to facilitator training sessions.</p>}
            </>
          ) : (
            <p>Register your interest to be invited to facilitator training sessions.</p>
          )}
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
        {interest === null ? (
          <Loader className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <button
            onClick={toggle}
            disabled={saving}
            className={`flex-shrink-0 rounded-xl h-10 px-5 text-sm font-bold transition-colors disabled:opacity-60 ${
              interest.interested
                ? 'border border-[#e2e8f0] bg-white text-slate-600 hover:bg-slate-50'
                : 'bg-primary text-white hover:bg-primary-dark shadow-sm'
            }`}
          >
            {saving ? 'Saving…' : interest.interested ? 'Withdraw interest' : "I'm interested"}
          </button>
        )}
      </div>
    </section>
  );
}
